import React from 'react'

import { Body2, Box, Color, Text, TextAlign, withCitrusTheme } from '@gousto-internal/citrus-react'
import { useSelector } from 'react-redux'

import type { RecipeQueryResponse } from '@library/api-menu-experience'
import type { MenuRecipeImage, RecipeImage as LegacyRecipeImage } from '@library/menu-service'

import { getBrowserType } from 'selectors/browser'
import { getFeaturedImage, getRangeImages } from 'utils/image'

import { DetailCarousel } from '../Carousel'
import { DetailImage } from '../DetailImage'

type RecipeImageProps = {
  title: string
  isHeatOnly?: boolean
  images: {
    partial?: LegacyRecipeImage[]
    full?: MenuRecipeImage[]
    experience?: RecipeQueryResponse['images']
  }
}

const ImageContainer = withCitrusTheme(Box, (theme) => ({
  position: 'relative',
  marginLeft: `-${theme.spacing(4)}px`,
  marginTop: `-${theme.spacing(4)}px`,
  width: `calc(100% + 2*${theme.spacing(4)}px)`,
  [`@media (min-width: ${theme.tokens.breakpoints[0]})`]: {
    marginLeft: `-${theme.spacing(6)}px`,
    marginTop: `-${theme.spacing(6)}px`,
    width: `calc(100% + 2*${theme.spacing(6)}px)`,
  },
}))

const HeatOnlyBanner = withCitrusTheme(Box, (theme) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  padding: `${theme.spacing(2)}px 0`,
  background: theme.color(Color.Warning_900),
  color: theme.color(Color.White),
}))

const Separator = () => (
  <Body2 aria-hidden="true" color={Color.White} size={2} style={{ whiteSpace: 'nowrap' }}>
    |
  </Body2>
)

const BannerText = ({ text }: { text: string }) => (
  <Text size={1} textAlign={TextAlign.Center} style={{ flexGrow: 1 }}>
    {text}
  </Text>
)

export const RecipeImage = ({ images, title, isHeatOnly = false }: RecipeImageProps) => {
  const { partial: partialImages, full: fullImages, experience: experienceImages } = images
  const browserType = useSelector(getBrowserType)

  const featuredImage =
    experienceImages?.[0]?.crops || getFeaturedImage(partialImages, 'standard', browserType)
  const fallbackFeaturedImage =
    featuredImage.length > 0 ? featuredImage : fullImages?.[0]?.crops || []
  const rangeImages =
    experienceImages && experienceImages.length > 1 ? experienceImages : getRangeImages(fullImages)

  return (
    <ImageContainer>
      {rangeImages.length > 0 ? (
        <DetailCarousel images={rangeImages} />
      ) : (
        <>
          <DetailImage media={fallbackFeaturedImage} title={title} />
          {isHeatOnly && (
            <HeatOnlyBanner data-testid="heat-only-banner">
              <BannerText text="HEAT & EAT" />
              <Separator />
              <BannerText text="Cooked by us" />
              <Separator />
              <BannerText text="No nasties" />
            </HeatOnlyBanner>
          )}
        </>
      )}
    </ImageContainer>
  )
}
